import { apiClient, handleApiRequest } from './client'

export interface EventoSettings {
  evento_api_url: string | null
  evento_api_key: string | null
  evento_sync_enabled: boolean
  evento_sync_frequency: string
  evento_last_sync_at: string | null
  evento_sync_staff_enabled: boolean
  evento_sync_timetable_enabled: boolean
  evento_sync_calendar_enabled: boolean
  evento_sync_substitutions_enabled: boolean
  evento_sync_strategy: string
  evento_webhook_secret: string | null
  qwen_api_key: string | null
}

export interface SyncLogEntry {
  id: string
  sync_type: string
  triggered_by: string
  status: string
  started_at: string
  completed_at: string | null
  duration_seconds: number | null
  staff_results: Record<string, number> | null
  timetable_results: Record<string, number> | null
  calendar_results: Record<string, number> | null
  substitution_results: Record<string, number> | null
  errors: string[] | null
  warnings: string[] | null
  created_at: string
}

export interface SyncStatus {
  enabled: boolean
  api_url_configured: boolean
  sync_frequency: string
  strategy: string
  last_sync_at: string | null
  sync_types: Record<string, boolean>
  current_sync?: {
    status: string
    percent: number
    staff_completed: number
    staff_total: number
    timetable_completed: number
    timetable_total: number
  }
}

export interface WebhookStatus {
  webhook_enabled: boolean
  webhook_url: string
  secret_configured: boolean
  api_key_configured: boolean
  last_events: string[]
}

export interface TestConnectionResult {
  success: boolean
  status_code: number
  message: string
  school_info?: Record<string, unknown>
}

export interface SyncTriggerResult {
  success: boolean
  sync_type: string
  records_synced: number
  status: string
  log_id?: string
}

export type SyncType = 'full' | 'staff_only' | 'timetable_only' | 'calendar_only' | 'substitutions_only'

export const eventoSyncApi = {
  getSettings: async (): Promise<EventoSettings> => {
    const res = await handleApiRequest<{ config: EventoSettings }>(apiClient.get('/api/v1/config/integration'))
    return res.config
  },

  updateSettings: async (payload: Partial<EventoSettings>): Promise<void> => {
    await handleApiRequest(apiClient.put('/api/v1/config/integration', { data: payload }))
  },

  testConnection: async (apiUrl?: string, apiKey?: string): Promise<TestConnectionResult> => {
    return handleApiRequest(apiClient.post('/api/v1/pdf-parser/evento/test', {
      api_url: apiUrl,
      api_key: apiKey,
    }))
  },

  triggerSync: async (syncType: SyncType = 'full'): Promise<SyncTriggerResult> => {
    return handleApiRequest(apiClient.post(`/api/v1/pdf-parser/evento/sync?sync_type=${syncType}`))
  },

  getSyncStatus: async (): Promise<SyncStatus> => {
    return handleApiRequest(apiClient.get('/api/v1/pdf-parser/evento/sync/status'))
  },

  getSyncHistory: async (limit: number = 50): Promise<{ sync_logs: SyncLogEntry[]; total: number }> => {
    return handleApiRequest(apiClient.get(`/api/v1/pdf-parser/evento/sync/history?limit=${limit}`))
  },

  getSyncLogDetails: async (logId: string): Promise<SyncLogEntry> => {
    return handleApiRequest(apiClient.get(`/api/v1/pdf-parser/evento/sync/history/${logId}`))
  },

  getWebhookStatus: async (): Promise<WebhookStatus> => {
    return handleApiRequest(apiClient.get('/api/v1/evento/webhook/status'))
  },

  updateWebhookSecret: async (secret: string): Promise<void> => {
    await handleApiRequest(apiClient.put('/api/v1/config/integration', { data: { evento_webhook_secret: secret } }))
  },

  dryRunSync: async (syncType: SyncType = 'full'): Promise<DryRunResult> => {
    return handleApiRequest(apiClient.post('/api/v1/evento/sync/dry-run', { sync_type: syncType }))
  },

  testWebhook: async (): Promise<WebhookTestResult> => {
    return handleApiRequest(apiClient.post('/api/v1/evento/webhook/test', {}))
  },

  toggleScheduledSync: async (enabled: boolean): Promise<{ success: boolean; scheduled_enabled: boolean; message: string }> => {
    return handleApiRequest(apiClient.post(`/api/v1/evento/sync/scheduled/toggle?enabled=${enabled}`))
  },
}

export interface DryRunResult {
  success: boolean
  would_create: number
  would_update: number
  would_skip: number
  would_delete: number
  conflicts: Array<{ type: string; message: string; data?: Record<string, unknown> }>
  warnings: string[]
  duration_estimate_seconds: number
}

export interface WebhookTestResult {
  success: boolean
  message: string
  received_at?: string
}